import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AppShell } from '~/components/layout/app-shell'
import { ErrorBanner } from '~/components/ui/error-banner'
import { Badge } from '~/components/ui/badge'
import {
    ClassificationBadge,
    PassLikelihoodBadge,
    StatusBadgeAssessment,
} from '~/components/assessment/assessment-badges'
import { scaleLabel } from '~/components/assessment/labels'
import {
    createReport,
    deleteAssessment,
    getAnswers,
    getAssessment,
    getChecklist,
    getReport,
    getScores,
    scoreAssessment,
} from '~/api/assessment'
import { errMessage } from '~/lib/errors'
import type {
    AssessmentReport,
    AssessmentResponse,
    ChecklistItem,
    ItemScore,
    SurveyAnswer,
    SurveyTurn,
} from '~/api/types'
import { CollectingView } from '~/features/certification/collecting-view'
import { ResultsView } from '~/features/certification/results-view'
import { formatJumpTargetMessage } from '~/features/certification/utils'
import type { JumpTarget } from '~/features/certification/types'

/** /certification/:id — 상태별 뷰: collecting=대화형 설문, scored/reported=결과. */
export function CertificationDetailPage() {
    const { id = '' } = useParams()
    const navigate = useNavigate()

    const [assessment, setAssessment] = useState<AssessmentResponse | null>(
        null
    )
    const [checklist, setChecklist] = useState<ChecklistItem[] | null>(null)
    const [scores, setScores] = useState<ItemScore[]>([])
    const [report, setReport] = useState<AssessmentReport | null>(null)
    const [answers, setAnswers] = useState<SurveyAnswer[]>([])

    // 대화형 설문 진행 상태(우측 패널 동기화용).
    const [surveyTurnState, setSurveyTurnState] = useState<SurveyTurn | null>(
        null
    )

    const [loadError, setLoadError] = useState<string | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const [busy, setBusy] = useState<'score' | 'report' | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [jumpTarget, setJumpTarget] = useState<JumpTarget | null>(null)

    const loadScores = useCallback(async () => {
        try {
            const list = await getScores(id)
            setScores(list)
        } catch {
            // 채점 전이면 비어 있을 수 있다. 무시.
        }
    }, [id])

    const loadAnswers = useCallback(async () => {
        try {
            setAnswers(await getAnswers(id))
        } catch {
            // 답안이 아직 없을 수 있다. 무시.
        }
    }, [id])

    // 설문 한 턴이 끝날 때마다 우측 패널의 답안 목록을 갱신한다.
    const handleTurn = useCallback(
        (turn: SurveyTurn, options?: { refreshAnswers?: boolean }) => {
            setSurveyTurnState(turn)
            if (options?.refreshAnswers !== false) {
                void loadAnswers()
            }
        },
        [loadAnswers]
    )

    useEffect(() => {
        if (!id) return
        let cancelled = false
        setLoadError(null)
        setAssessment(null)
        setChecklist(null)
        setScores([])
        setReport(null)
        setAnswers([])
        setSurveyTurnState(null)

        void (async () => {
            try {
                const assessmentPromise = getAssessment(id)
                const checklistPromise = getChecklist(id).catch(() => [])
                const answersPromise = getAnswers(id).catch(() => [])

                const a = await assessmentPromise
                if (cancelled) return
                setAssessment(a)

                checklistPromise.then((items) => {
                    if (!cancelled) setChecklist(items)
                })
                answersPromise.then((currentAnswers) => {
                    if (!cancelled) setAnswers(currentAnswers)
                })

                const [items, currentAnswers] = await Promise.all([
                    checklistPromise,
                    answersPromise,
                ])
                if (cancelled) return
                setChecklist(items)
                setAnswers(currentAnswers)

                if (a.status !== 'collecting') {
                    await loadScores()
                }
                if (a.status === 'reported') {
                    try {
                        const r = await getReport(id)
                        if (!cancelled) setReport(r)
                    } catch {
                        // 보고서가 아직 없을 수 있다.
                    }
                }
            } catch (err: unknown) {
                if (!cancelled)
                    setLoadError(errMessage(err, '평가를 불러오지 못했어요.'))
            }
        })()

        return () => {
            cancelled = true
        }
    }, [id, loadScores, loadAnswers])

    const handleScore = useCallback(async () => {
        setBusy('score')
        setActionError(null)
        try {
            const updated = await scoreAssessment(id)
            setAssessment(updated)
            await loadScores()
        } catch (err: unknown) {
            setActionError(errMessage(err, '채점에 실패했어요.'))
        } finally {
            setBusy(null)
        }
    }, [id, loadScores])

    const handleReport = useCallback(async () => {
        setBusy('report')
        setActionError(null)
        try {
            const r = await createReport(id)
            setReport(r)
            const updated = await getAssessment(id)
            setAssessment(updated)
        } catch (err: unknown) {
            setActionError(errMessage(err, '보고서 생성에 실패했어요.'))
        } finally {
            setBusy(null)
        }
    }, [id])

    const handleDelete = useCallback(async () => {
        if (!assessment) return
        const ok = window.confirm(
            `"${assessment.title}" 평가를 삭제할까요?\n답변, 채점 결과, 보고서도 함께 삭제됩니다.`
        )
        if (!ok) return
        setDeleting(true)
        setActionError(null)
        try {
            await deleteAssessment(id)
            navigate('/certification', { replace: true })
        } catch (err: unknown) {
            setActionError(errMessage(err, '평가 삭제에 실패했어요.'))
        } finally {
            setDeleting(false)
        }
    }, [assessment, id, navigate])

    const collecting = assessment ? assessment.status === 'collecting' : false
    // 채점·보고서 완료 후에도 "평가 수정하기"로 설문을 다시 열 수 있게 한다.
    const [editing, setEditing] = useState(false)
    const showSurvey = collecting || editing

    return (
        <AppShell>
            <div className='h-full overflow-hidden'>
                <div className='mx-auto flex h-full w-full max-w-[1680px] flex-col overflow-hidden px-4 py-4 lg:px-8 2xl:px-10'>
                    <div className='mb-3 shrink-0'>
                        <Link
                            to='/certification'
                            className='text-primary text-sm hover:underline'>
                            ← 평가 목록
                        </Link>
                    </div>

                    {loadError ? (
                        <ErrorBanner className='mb-3 shrink-0'>
                            {loadError}
                        </ErrorBanner>
                    ) : null}

                    {!assessment ? (
                        !loadError ? (
                            <p className='text-muted-foreground text-sm'>
                                평가를 불러오는 중…
                            </p>
                        ) : null
                    ) : (
                        <>
                            <div className='mb-3 flex shrink-0 flex-wrap items-center gap-2'>
                                <h1 className='text-foreground min-w-0 text-lg font-semibold'>
                                    {assessment.title}
                                </h1>
                                <StatusBadgeAssessment
                                    status={assessment.status}
                                />
                                <ClassificationBadge
                                    classification={assessment.classification}
                                />
                                {assessment.assessmentType ? (
                                    <Badge tone='neutral'>
                                        {assessment.assessmentType}
                                    </Badge>
                                ) : null}
                                {assessment.passLikelihood ? (
                                    <PassLikelihoodBadge
                                        likelihood={assessment.passLikelihood}
                                    />
                                ) : null}
                                <span className='text-muted-foreground text-sm'>
                                    {scaleLabel(assessment.scale)}
                                    {assessment.industry
                                        ? ` · ${assessment.industry}`
                                        : ''}
                                    {assessment.totalScore != null &&
                                    assessment.maxScore != null
                                        ? ` · ${assessment.totalScore}/${assessment.maxScore}점`
                                        : ''}
                                </span>
                            </div>

                            {actionError ? (
                                <ErrorBanner className='mb-3 shrink-0'>
                                    {actionError}
                                </ErrorBanner>
                            ) : null}

                            <div className='min-h-0 flex-1 overflow-hidden'>
                                {showSurvey ? (
                                    <CollectingView
                                        assessment={assessment}
                                        assessmentId={id}
                                        turn={surveyTurnState}
                                        checklist={checklist}
                                        answers={answers}
                                        onTurn={handleTurn}
                                        onAnswersSaved={() => {
                                            void loadAnswers()
                                        }}
                                        onScore={async () => {
                                            await handleScore()
                                            setEditing(false)
                                        }}
                                        onDelete={handleDelete}
                                        deleting={deleting}
                                        scoring={busy === 'score'}
                                        jumpTarget={jumpTarget}
                                        onSelectItem={(itemId) => {
                                            const item = checklist?.find(
                                                (entry) => entry.id === itemId
                                            )
                                            setJumpTarget({
                                                itemId,
                                                message:
                                                    item != null
                                                        ? formatJumpTargetMessage(
                                                              assessment,
                                                              item
                                                          )
                                                        : '요청한 체크리스트 항목으로 이동합니다.',
                                                nonce: Date.now(),
                                            })
                                        }}
                                    />
                                ) : (
                                    <ResultsView
                                        assessment={assessment}
                                        scores={scores}
                                        checklist={checklist}
                                        answers={answers}
                                        report={report}
                                        onReport={handleReport}
                                        reporting={busy === 'report'}
                                        onEdit={() => setEditing(true)}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </AppShell>
    )
}
